import { createStore } from "zustand/vanilla";
import { subscribeWithSelector } from "zustand/middleware";
const SIDEBAR_PREFERENCE_KEY = "leadpilot/preferSidebarCollapsed";
const THEME_PREFERENCE_KEY = "leadpilot/theme";
const SYSTEM_THEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";
export const SIDEBAR_STATE_CLASS = {
    expanded: "expanded",
    collapsed: "collapsed",
};
export const THEME = {
    dark: "dark",
    light: "light",
    system: "system",
};
const THEMES = [THEME.dark, THEME.light, THEME.system];
function getPersistedSidebarCollapsedPreference() {
    try {
        const value = window.localStorage.getItem(SIDEBAR_PREFERENCE_KEY);
        if (value === "true") {
            return true;
        }
        if (value === "false") {
            return false;
        }
    }
    catch (error) {
        // Ignore storage access errors and fallback to the DOM state.
    }
    return null;
}
function persistSidebarCollapsedPreference(isCollapsed) {
    try {
        window.localStorage.setItem(SIDEBAR_PREFERENCE_KEY, isCollapsed ? "true" : "false");
    }
    catch (error) {
        // Ignore storage access errors.
    }
}
function isTheme(theme) {
    return THEMES.some((option) => option === theme);
}
function getPersistedThemePreference() {
    try {
        const value = window.localStorage.getItem(THEME_PREFERENCE_KEY);
        if (isTheme(value)) {
            return value;
        }
    }
    catch (error) {
        // Ignore storage access errors and fallback to the browser preference.
    }
    return null;
}
function getPreferredColorScheme() {
    if (typeof window.matchMedia === "function") {
        return window.matchMedia(SYSTEM_THEME_MEDIA_QUERY).matches
            ? THEME.dark
            : THEME.light;
    }
    const currentTheme = document.documentElement.dataset.theme;
    return currentTheme === THEME.light ? THEME.light : THEME.dark;
}
function getResolvedTheme(theme) {
    return theme === THEME.system ? getPreferredColorScheme() : theme;
}
export function getThemeToggleTarget(theme) {
    const currentIndex = THEMES.indexOf(theme);
    return THEMES[(currentIndex + 1) % THEMES.length];
}
function persistThemePreference(theme) {
    try {
        window.localStorage.setItem(THEME_PREFERENCE_KEY, theme);
    }
    catch (error) {
        // Ignore storage access errors.
    }
}
export function applyThemeToDocument(theme) {
    const root = document.documentElement;
    const resolvedTheme = getResolvedTheme(theme);
    root.dataset.theme = resolvedTheme;
    root.style.colorScheme = resolvedTheme;
}
function bindSystemThemeListener(getTheme) {
    if (typeof window.matchMedia !== "function") {
        return;
    }
    window.matchMedia(SYSTEM_THEME_MEDIA_QUERY).addEventListener("change", () => {
        if (getTheme() === THEME.system) {
            applyThemeToDocument(THEME.system);
        }
    });
}
export function getSidebarStateClass(isSidebarOpen) {
    return isSidebarOpen
        ? SIDEBAR_STATE_CLASS.expanded
        : SIDEBAR_STATE_CLASS.collapsed;
}
export const store = createStore()(subscribeWithSelector((set, get) => ({
    isSidebarOpen: true,
    isMobileMenuOpen: false,
    hasInitializedSidebar: false,
    theme: THEME.system,
    hasInitializedTheme: false,
    initializeSidebar: (fallbackIsOpen) => {
        if (get().hasInitializedSidebar) {
            return;
        }
        const preferredCollapsed = getPersistedSidebarCollapsedPreference();
        set({
            isSidebarOpen: preferredCollapsed == null
                ? fallbackIsOpen
                : !preferredCollapsed,
            hasInitializedSidebar: true,
        });
    },
    setSidebarOpen: (isOpen, persistPreference = true) => {
        if (persistPreference) {
            persistSidebarCollapsedPreference(!isOpen);
        }
        set({
            isSidebarOpen: isOpen,
            hasInitializedSidebar: true,
        });
    },
    toggleSidebar: () => {
        get().setSidebarOpen(!get().isSidebarOpen);
    },
    setMobileMenuOpen: (isOpen) => {
        set({ isMobileMenuOpen: isOpen });
    },
    toggleMobileMenu: () => {
        get().setMobileMenuOpen(!get().isMobileMenuOpen);
    },
    initializeTheme: () => {
        if (get().hasInitializedTheme) {
            applyThemeToDocument(get().theme);
            return;
        }
        bindSystemThemeListener(() => get().theme);
        const theme = getPersistedThemePreference() ?? THEME.system;
        applyThemeToDocument(theme);
        set({
            theme,
            hasInitializedTheme: true,
        });
    },
    setTheme: (theme, persistPreference = true) => {
        if (persistPreference) {
            persistThemePreference(theme);
        }
        applyThemeToDocument(theme);
        set({
            theme,
            hasInitializedTheme: true,
        });
    },
    toggleTheme: () => {
        get().setTheme(getThemeToggleTarget(get().theme));
    },
})));
